import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { IconLink } from '../../Icon';
import { ShareIcon } from '../../ShareIcon';
import { LineItemFavoriteBtn } from './LineItemFavoriteBtn';
import { LineItemQuantity } from './LineItemQuantity';
import { LineItemRemoveBtn } from './LineItemRemoveBtn';

// ────────────────────────────────────────────────────────────────────────────────

export const LineItemFooter = (props: ProductFooterProps) => {
  const {
    quantity = 0,
    onQuantityChange,
    isFavorite,
    isFetchingFavorite,
    shareBtnLabel,
    shareTooltipLabel,
    productId,
    removeBtnLabel,
    isDisabled,
    productSlug,
    onFavorite,
    onShare,
    onRemove,
    isAddingToBag,
    onAddToBag,
  } = props;

  // ──────────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const addToBagA11y = formatMessage(messages.addToBagA11y);

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.wrapper}>
      <View style={styles.row}>
        {quantity > 1 ? (
          <View style={styles.stackItem}>
            <LineItemQuantity
              isDisabled={isDisabled}
              quantity={quantity}
              onQuantityChange={onQuantityChange}
              productSlug={productSlug}
            />
          </View>
        ) : null}

        {onFavorite && productId ? (
          <View style={styles.stackItem}>
            <LineItemFavoriteBtn
              isActive={isFavorite}
              disabled={isFetchingFavorite}
              onPress={onFavorite}
            />
          </View>
        ) : null}

        {onShare && productId ? (
          <View style={styles.stackItem}>
            <ShareIcon
              shareBtnLabel={shareBtnLabel}
              shareTooltipLabel={shareTooltipLabel}
              onShare={onShare}
            />
          </View>
        ) : null}

        {onAddToBag && productId ? (
          <View style={styles.stackItem}>
            <IconLink
              width={24}
              height={24}
              color={theme.colors.KALE}
              name="IconAddToBag"
              isLoading={isAddingToBag}
              accessibilityRole="button"
              accessibilityLabel={addToBagA11y}
              onPress={onAddToBag}
            />
          </View>
        ) : null}
      </View>

      {onRemove ? (
        <LineItemRemoveBtn
          productId={productId}
          accessibilityLabel={removeBtnLabel}
          disabled={isDisabled}
          testID={`sg-line-item-card-remove-${productSlug}`}
          onPress={onRemove}
        />
      ) : null}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addToBagA11y: {
    defaultMessage: 'Adds line item to bag',
    description: 'Line Item Footer | Add to bag | Accessibility label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: theme.spacing['4'],
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
  },
  stackItem: {
    width: 40,
    height: 40,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductFooterProps = Readonly<{
  productId: string;
  productSlug: string;
  quantity?: number;
  removeBtnLabel?: string;
  shareTooltipLabel?: string;
  shareBtnLabel?: string;
  isFetchingFavorite?: boolean;
  isFavorite?: boolean;
  isDisabled?: boolean;
  onRemove?: (productId: string) => void;
  onShare?: () => void;
  onFavorite?: () => void;
  onQuantityChange?:
    | ((selectedQuantity: number) => void)
    | ((selectedQuantity: number) => Promise<void>);
  isAddingToBag?: boolean;
  onAddToBag?: () => void;
}>;
